import { useCallback, useEffect, useMemo, useState } from 'react';
import { Loader2 } from 'lucide-react';
import { api } from '../../lib/api';

// Configuracoes → AppCel → aba "Log de uso": a linha do tempo de cada cliente dentro
// do aplicativo.
//
// Pedido dele em 02/09/2026, com a finalidade escrita: *"entender qual o comportamento
// do usuario no app para aplicar as melhorias"*. Por isso a tela nao e' um extrato de
// visitas — ela MISTURA o que a pessoa abriu com o que ela fez (orcamento montado,
// compromisso marcado, mensagem escrita, nota baixada), que foi a escolha dele quando
// perguntei ("Sim, misturados").
//
// ⚠️ **Nao ha gravacao nova por tras desta tela.** As acoes saem das tabelas de
// negocio e so a VISITA tem tabela propria ([DA-411]/[DA-412] — auditoria sai da
// tabela de negocio quando ha onde pendurar o carimbo). Ver `app-metricas.routes.ts`.
//
// ⚠️ **Visita e' medida desde 25/08/2026 e acao vem desde sempre** — as duas metades
// tem alcances diferentes, e o rodape diz isso. Sem essa linha, o silencio de antes de
// 25/08 le como "ninguem usou" quando significa "ninguem media" ([DA-468]: ausencia
// nao se distingue de inexistencia).

interface EventoLog {
  quando: string;
  tipo: 'visita' | 'orcamento' | 'compromisso' | 'mensagem';
  detalhe: string;
  classe: 'visita' | 'acao';
}

interface ContaLog {
  id: number;
  nome: string;
  total: number;
  ultima: string | null;
  eventos: EventoLog[];
}

interface RespostaLog {
  dias: number;
  desde_visitas: string | null;
  contas: ContaLog[];
}

type Periodo = 7 | 15 | 0;
type Mostrar = 'tudo' | 'visitas' | 'acoes';

const CHAVE_PERIODO = 'appcel.log.periodo';
const CHAVE_MOSTRAR = 'appcel.log.mostrar';

/** Etiqueta de cada especie. ⛔ Verde/vermelho nao entram (daltonismo) — e a PALAVRA
 *  vai junto da cor, senao o sinal depende de distinguir dois tons de cinza. */
const ETIQUETA: Record<EventoLog['tipo'], { texto: string; fundo: string; frente: string }> = {
  visita: { texto: 'VISITA', fundo: '#e2e8f0', frente: '#334155' },
  orcamento: { texto: 'ORÇAMENTO', fundo: '#fde68a', frente: '#000000' },
  compromisso: { texto: 'COMPROMISSO', fundo: 'var(--primary)', frente: '#ffffff' },
  mensagem: { texto: 'MENSAGEM', fundo: '#ffffff', frente: '#334155' },
};

/** O que a area quer dizer pra quem le — nunca o nome tecnico da coluna. */
const AREA: Record<string, string> = {
  home: 'abriu o app (menu)',
  chat: 'abriu o Chat',
  orcamentos: 'abriu Orçamentos',
  agenda: 'abriu a Agenda',
  financeiro: 'abriu Contas a Pagar',
  notas_fiscais: 'abriu Notas Fiscais',
  nota: 'baixou uma nota (PDF)',
};

function frase(e: EventoLog): string {
  if (e.tipo === 'visita') return AREA[e.detalhe] ?? e.detalhe;
  if (e.tipo === 'orcamento') return `montou um orçamento${e.detalhe ? ` — ${e.detalhe}` : ''}`;
  if (e.tipo === 'compromisso') return `marcou compromisso — ${e.detalhe}`;
  return `escreveu: “${e.detalhe}”`;
}

function dia(iso: string): string {
  return new Date(iso).toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit' });
}

function hora(iso: string): string {
  return new Date(iso).toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' });
}

function dataHoraCurta(iso: string | null): string {
  if (!iso) return '—';
  return `${dia(iso)} ${hora(iso)}`;
}

export default function LogUsoAppCel() {
  // Lazy init lendo o localStorage: filtro de tela persiste (convencao da casa).
  //
  // ⛔ **Ler a string ANTES de converter.** `Number(null)` e' **0**, e 0 aqui e' um
  // periodo LEGITIMO ("Tudo") — entao `Number(localStorage.getItem(...))` faz a
  // AUSENCIA de preferencia virar uma escolha, e a aba abre na serie inteira em vez
  // dos 7 dias. Pego apertando o botao na release publicada em 02/09/2026: a tela
  // subiu com "Tudo" aceso e ninguem tinha escolhido nada. Vale pra todo filtro
  // guardado cujo conjunto de valores validos inclui 0 ou string vazia.
  const [periodo, setPeriodo] = useState<Periodo>(() => {
    const bruto = localStorage.getItem(CHAVE_PERIODO);
    if (bruto === null) return 7;
    const v = Number(bruto);
    return v === 7 || v === 15 || v === 0 ? (v as Periodo) : 7;
  });
  const [mostrar, setMostrar] = useState<Mostrar>(() => {
    const v = localStorage.getItem(CHAVE_MOSTRAR);
    return v === 'visitas' || v === 'acoes' ? v : 'tudo';
  });
  const [dados, setDados] = useState<RespostaLog | null>(null);
  const [erro, setErro] = useState<string | null>(null);
  const [escolhida, setEscolhida] = useState<number | null>(null);

  useEffect(() => { localStorage.setItem(CHAVE_PERIODO, String(periodo)); }, [periodo]);
  useEffect(() => { localStorage.setItem(CHAVE_MOSTRAR, mostrar); }, [mostrar]);

  const carregar = useCallback(async () => {
    try {
      setDados(await api.get<RespostaLog>(`/api/app-metricas/log?dias=${periodo}`));
      setErro(null);
    } catch (e) {
      setErro((e as Error).message || 'Nao consegui carregar o log agora.');
    }
  }, [periodo]);

  useEffect(() => { void carregar(); }, [carregar]);

  /** Os eventos que a tela esta MOSTRANDO — e' deles que sai todo numero visivel.
   *  ⚠️ Contador ao lado de um filtro promete ser o resultado daquele filtro
   *  ([DA-597]): contar o total do servidor aqui faria a lista dizer 9 e desenhar 4. */
  const visiveis = useCallback(
    (c: ContaLog) => (mostrar === 'tudo' ? c.eventos : c.eventos.filter((e) => (mostrar === 'visitas' ? e.classe === 'visita' : e.classe === 'acao'))),
    [mostrar],
  );

  /**
   * ⛔ **A DATA obedece ao filtro junto com o contador — os dois sao o mesmo numero
   * dito de duas formas.** Com "So acoes" ligado, usar o `ultima` que o servidor
   * mandou (o ultimo evento de QUALQUER especie) fazia a linha dizer
   * *"ultima atividade 01/09 21:41 · 2 registros"* pra uma conta cujas 2 acoes sao de
   * 11:25 — as 21:41 ela so tinha ABERTO o app. Contador certo, carimbo mentindo:
   * exatamente a metade que a [DA-597] pega. Achado na release publicada em
   * 02/09/2026, clicando o filtro. Eventos vem em ordem crescente, entao o ultimo
   * visivel e' o fim da lista.
   */
  const ultimaVisivel = useCallback(
    (c: ContaLog) => {
      const evs = visiveis(c);
      return evs[evs.length - 1]?.quando ?? null;
    },
    [visiveis],
  );

  const contas = dados?.contas ?? [];
  const atual = useMemo(
    () => contas.find((c) => c.id === escolhida) ?? contas[0] ?? null,
    [contas, escolhida],
  );

  const totalVisivel = useMemo(
    () => contas.reduce((s, c) => s + visiveis(c).length, 0),
    [contas, visiveis],
  );

  if (erro) {
    return (
      <div style={{ backgroundColor: '#ef4444', color: '#000', padding: '10px 14px', borderRadius: 8, fontSize: 14 }}>
        {erro}
      </div>
    );
  }

  if (!dados) {
    return (
      <p style={{ display: 'flex', alignItems: 'center', gap: 8, color: 'var(--text-color)' }}>
        <Loader2 size={16} className="animate-spin" /> Carregando...
      </p>
    );
  }

  const eventosAtual = atual ? visiveis(atual) : [];
  // Agrupa por dia mantendo a ordem que o servidor mandou (crescente).
  const porDia: Array<{ dia: string; eventos: EventoLog[] }> = [];
  eventosAtual.forEach((e) => {
    const d = dia(e.quando);
    const ultimo = porDia[porDia.length - 1];
    if (ultimo && ultimo.dia === d) ultimo.eventos.push(e);
    else porDia.push({ dia: d, eventos: [e] });
  });

  return (
    <div style={{ fontSize: 13 }}>
      <div style={{ display: 'flex', gap: 16, alignItems: 'center', marginBottom: 12, flexWrap: 'wrap' }}>
        <Grupo rotulo="PERÍODO">
          <Pastilha ativa={periodo === 7} onClick={() => setPeriodo(7)}>7 dias</Pastilha>
          <Pastilha ativa={periodo === 15} onClick={() => setPeriodo(15)}>15 dias</Pastilha>
          <Pastilha ativa={periodo === 0} onClick={() => setPeriodo(0)}>Tudo</Pastilha>
        </Grupo>
        <Grupo rotulo="MOSTRAR">
          <Pastilha ativa={mostrar === 'tudo'} onClick={() => setMostrar('tudo')}>Tudo</Pastilha>
          <Pastilha ativa={mostrar === 'visitas'} onClick={() => setMostrar('visitas')}>Só visitas</Pastilha>
          <Pastilha ativa={mostrar === 'acoes'} onClick={() => setMostrar('acoes')}>Só ações</Pastilha>
        </Grupo>
        <div style={{ marginLeft: 'auto', fontSize: 12, color: 'var(--text-muted)' }}>
          {contas.length} contas de cliente · {totalVisivel} registros
        </div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0, 330px) minmax(0, 1fr)', gap: 14, alignItems: 'start' }}>
        <div style={{ backgroundColor: 'var(--panel-bg)', border: '1px solid var(--border)', borderRadius: 10, overflow: 'hidden' }}>
          {contas.map((c) => {
            const sel = atual?.id === c.id;
            const n = visiveis(c).length;
            return (
              <button
                key={c.id}
                type="button"
                onClick={() => setEscolhida(c.id)}
                style={{
                  display: 'block', width: '100%', textAlign: 'left',
                  padding: '9px 12px', borderBottom: '1px solid var(--page-bg)',
                  backgroundColor: sel ? 'var(--primary)' : 'transparent',
                  color: sel ? '#fff' : 'var(--text-color)',
                }}
              >
                <div style={{ fontWeight: 600 }}>{c.nome}</div>
                <div style={{ fontSize: 11, opacity: sel ? 0.9 : 0.75 }}>
                  {n === 0 ? 'nada no período' : `última atividade ${dataHoraCurta(ultimaVisivel(c))} · ${n} registros`}
                </div>
              </button>
            );
          })}
        </div>

        <div style={{ backgroundColor: 'var(--panel-bg)', border: '1px solid var(--border)', borderRadius: 10, padding: '14px 16px', minHeight: 420 }}>
          {!atual ? (
            <p style={{ color: 'var(--text-muted)' }}>Nenhuma conta de cliente cadastrada.</p>
          ) : (
            <>
              <div style={{ fontWeight: 700, fontSize: 15, color: 'var(--text-color)' }}>{atual.nome}</div>
              <div style={{ fontSize: 11, color: 'var(--text-muted)', marginBottom: 10 }}>
                {eventosAtual.length} registros · última atividade {dataHoraCurta(ultimaVisivel(atual))}
              </div>

              {porDia.length === 0 ? (
                // ⚠️ Vazio NOMEIA o filtro que o produziu — senao le como "esta pessoa
                // nunca usou o app", que e' outra coisa ([DA-468]).
                <p style={{ color: 'var(--text-muted)' }}>
                  Nada aqui {periodo === 0 ? 'na série inteira' : `nos últimos ${periodo} dias`}
                  {mostrar !== 'tudo' && ` (mostrando só ${mostrar === 'visitas' ? 'visitas' : 'ações'})`}.
                </p>
              ) : (
                porDia.map((g, i) => {
                  // ⚠️ A etiqueta so aparece com o filtro em "Tudo". Em "Só visitas"
                  // TODO dia seria "só olhou" por construcao — selo que nao pode dar
                  // outro valor nao mede nada, so parece medir.
                  const soOlhou = mostrar === 'tudo' && g.eventos.every((e) => e.classe === 'visita');
                  return (
                    <div key={g.dia}>
                      <div style={{
                        margin: i === 0 ? '0 0 6px' : '14px 0 6px', paddingBottom: 3,
                        fontWeight: 700, fontSize: 12, letterSpacing: '0.4px',
                        color: 'var(--text-color)', borderBottom: '1px solid var(--page-bg)',
                      }}>
                        {g.dia}
                        {soOlhou && (
                          <span style={{
                            marginLeft: 6, padding: '1px 8px', borderRadius: 999, fontSize: 10, fontWeight: 600,
                            backgroundColor: '#fff', color: '#334155', border: '1px solid var(--border)',
                          }}>
                            SÓ OLHOU
                          </span>
                        )}
                      </div>
                      {g.eventos.map((e, j) => {
                        const et = ETIQUETA[e.tipo];
                        return (
                          <div key={`${e.quando}-${j}`} style={{ display: 'grid', gridTemplateColumns: '52px 112px minmax(0, 1fr)', gap: 10, alignItems: 'center', padding: '4px 0' }}>
                            <div style={{ fontVariantNumeric: 'tabular-nums', color: 'var(--text-muted)' }}>{hora(e.quando)}</div>
                            <div style={{
                              fontSize: 10, fontWeight: 700, letterSpacing: '0.3px', textAlign: 'center',
                              padding: '2px 0', borderRadius: 4, border: '1px solid var(--border)',
                              backgroundColor: et.fundo, color: et.frente,
                            }}>
                              {et.texto}
                            </div>
                            <div style={{ color: 'var(--text-color)', overflowWrap: 'anywhere' }}>{frase(e)}</div>
                          </div>
                        );
                      })}
                    </div>
                  );
                })
              )}

              <div style={{ marginTop: 18, paddingTop: 10, borderTop: '1px solid var(--page-bg)', fontSize: 11, color: 'var(--text-muted)', lineHeight: 1.6 }}>
                As <b>visitas</b> são medidas desde {dados.desde_visitas ? new Date(dados.desde_visitas).toLocaleDateString('pt-BR') : 'hoje'} — o que
                aconteceu antes disso não foi registrado. As <b>ações</b> (orçamento, compromisso, mensagem, nota baixada) vêm desde
                que a conta nasceu. Cada área conta no máximo uma visita a cada 30 minutos, senão o Chat — que consulta o
                servidor a cada 4 segundos — encheria o log sozinho. Contas da casa ficam de fora.
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

function Grupo({ rotulo, children }: { rotulo: string; children: React.ReactNode }) {
  return (
    <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
      <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>{rotulo}</span>
      {children}
    </div>
  );
}

function Pastilha({ ativa, onClick, children }: { ativa: boolean; onClick: () => void; children: React.ReactNode }) {
  return (
    <button
      type="button"
      onClick={onClick}
      style={{
        padding: '3px 11px', borderRadius: 999, fontSize: 12,
        border: '1px solid var(--border)',
        backgroundColor: ativa ? 'var(--primary)' : '#fff',
        color: ativa ? '#fff' : 'var(--primary)',
        fontWeight: ativa ? 700 : 500,
      }}
    >
      {children}
    </button>
  );
}
